import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import {
  AppDialogSection,
  AppDialog,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogContent,
  AppDialogActions,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { LabelSmRg } from '@/UI/AppTypography/AppTypography';
import { EL_COLORS } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Input } from '@/UI/Input/Input';
import { FlexCol } from '@/UI/styles';
import { ZIndex } from '@/consts/z-indexes.consts';

interface CreateFolderModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (folderName: string) => void | Promise<void>;
}

export const CreateFolderModal = ({ open, onClose, onConfirm }: CreateFolderModalProps) => {
  const [folderName, setFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const handleConfirm = useCallback(async () => {
    if (!folderName.trim()) return;

    setIsLoading(true);
    try {
      await onConfirm(folderName.trim());
      setFolderName('');
      onClose();
    } catch (_error) {
      // Handle error silently
    } finally {
      setIsLoading(false);
    }
  }, [folderName, onConfirm, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleConfirm();
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      sx={{ zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS }}
      width="md"
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            borderRadius: 1,
            border: `1px solid ${EL_COLORS.BoxBorder}`,
            m: 0,
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 5.25, pb: 0, position: 'relative' }}>
          <AppDialogTitle>{t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.TITLE)}</AppDialogTitle>
          <AppDialogXBtn onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }} />
        </AppDialogHeader>

        <AppDialogContent sx={{ px: 3, pb: 0, '&&': { pt: 4 } }}>
          <FlexCol sx={{ gap: 0.5 }}>
            <LabelSmRg sx={{ color: token.text }}>
              {t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.NAME_LABEL)}
            </LabelSmRg>
            <Input
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </FlexCol>
        </AppDialogContent>

        <AppDialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 4 }}>
          <ButtonContained mode="outlined" onClick={onClose} size="small" sx={{ px: 2 }}>
            {t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.CANCEL)}
          </ButtonContained>
          <ButtonContained
            mode="filled-light"
            onClick={() => void handleConfirm()}
            disabled={isLoading || !folderName.trim()}
            size="small"
            sx={{ px: 2 }}
            loading={isLoading}
          >
            {t(I18N_KEYS.MAIN_DASHBOARD.CREATE_FOLDER_MODAL.CREATE)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
};
